import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, MessageSquare, Flame, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const FAQS = [
  {
    q: "Where is the legal entity / LLC / team background? Who is behind this?",
    a: "Sovereign engineering doesn't need a VC board or a Delaware corporation. The infrastructure speaks for itself. We are building direct-to-metal manufacturing capabilities that bypass legacy gatekeepers entirely. You are funding the physical hardware and the atomic printer, not a pitch deck or a management team.",
  },
  {
    q: "How do we know this atomic deposition printer actually works / isn't vaporware?",
    a: "That's exactly what the $1.75M capital raise is securing—the production-grade hardware itself. We are moving from finalized CAD blueprints and structural math to physical fabrication. If you want a safe SaaS investment with a PowerPoint presentation, look elsewhere. If you want to fund foundational hard tech from the ground up, the BTC address is open.",
  },
  {
    q: "What happens to the donations if the full $1.75M isn't raised immediately?",
    a: "Every satoshi sent to bc1qepqmkem807pu87ryw4pmjy9frqjwne0fpr0rsm is locked strictly into the hardware acquisition fund. Because we operate entirely on-chain without fiat overhead or intermediary fees, capital accumulates directly toward securing the multi-tip deposition printer components piece by piece.",
  },
  {
    q: "Can you share more details or specs on what's inside the vault?",
    a: "The public white papers and manifests in the SoverTech library lay out the baseline philosophy and structural frameworks. The core proprietary recipes and advanced vault specifications remain strictly locked. You are backing the sovereign execution of physical infrastructure, not buying a public share in a company.",
    link: { href: "/library", label: "Read the public white papers" },
  },
];

const X_THREAD = `Post 1/4
Stop funding software wrappers. It's time to back physical, atomic-scale hardware infrastructure.

We are moving past legacy silicon and fragile code. The future belongs to solid-state, biomimetic systems built from the molecule up.

Post 2/4
Our blueprint eliminates traditional wiring and external processors. Instead, we print multi-layered carbon-hydrogen-gold lattices, GaN neural cores, and carbon-nanotube muscle systems in a single unified atomic pass.

Post 3/4
To make this real, we are raising capital entirely on-chain. No VCs, no middle-men, no fiat bottlenecks.

Our initial target is $1,750,000 in BTC to secure the production-grade multi-tip atomic deposition printer and lock in our fabrication grid.

Post 4/4
Fund the core mechanics. Own the physical grid.

Send sats to support the build:
bc1qepqmkem807pu87ryw4pmjy9frqjwne0fpr0rsm

Read the full manifesto and track our progress at the SoverTech library: https://sovertech.org #Bitcoin #HardTech #DeepTech #AtomicScale #BuildInPublic`;

const TELEGRAM_DROP = `SoverTech | Atomic-Scale Fabrication | BTC Only

We are not building software. We are not issuing tokens.

We are raising $1,750,000 in Bitcoin to deploy a production-grade multi-tip atomic deposition printer, the physical infrastructure required to print carbon-hydrogen-gold structural lattices, GaN neural pathways, and carbon-nanotube muscle systems in a single unified build.

No VCs. No fiat. No gatekeepers.

Behind this raise sits a secured vault of advanced structural systems and fabrication frameworks built from the atomic layer up. What's public is a fraction of the architecture. The blueprints are locked. Execution begins now.

This is sovereign, hardware-backed engineering for people who understand foundational infrastructure.

BTC Wallet: bc1qepqmkem807pu87ryw4pmjy9frqjwne0fpr0rsm

Full Manifesto + White Papers: https://sovertech.org

Drop sats. Own the physical grid.`;

const LAUNCH_KIT = [
  {
    key: "x-thread",
    title: "X (Twitter) Launch Thread",
    desc: "Four posts, ready to fire in sequence. Hook, blueprint, the $1.75M ask, and the wallet close.",
    text: X_THREAD,
  },
  {
    key: "telegram",
    title: "Telegram Drop Message",
    desc: "High-signal single message for deep-tech, Bitcoin builder, and sovereign engineering groups.",
    text: TELEGRAM_DROP,
  },
  {
    key: "followup-why-btc",
    title: "Follow-Up 1 — Why Bitcoin Only (Day 3)",
    desc: "Answers the payment-rail question before it becomes an objection.",
    text: `Follow-up — Day 3

The most common question so far: "Why is this raise Bitcoin-only?"

Because hardware doesn't wait on banking rails. No processor freezes, no chargebacks, no fiat bottlenecks, no permission slips. Every satoshi lands directly in the hardware acquisition fund and moves the build forward the moment it confirms on-chain.

Target: $1,750,000 in BTC
Deliverable: production-grade multi-tip atomic deposition printer
Wallet: bc1qepqmkem807pu87ryw4pmjy9frqjwne0fpr0rsm

Fund the core mechanics. Own the physical grid.
https://sovertech.org/donate`,
  },
  {
    key: "followup-the-machine",
    title: "Follow-Up 2 — What The Machine Unlocks (Day 7)",
    desc: "Technical credibility post — points skeptics at the published QACLD framework.",
    text: `Follow-up — Day 7

What does a multi-tip atomic deposition printer actually unlock?

Single-pass deposition of carbon-hydrogen-gold structural lattices. GaN neural pathways. Carbon-nanotube muscle hinges. Solid-state systems with no soldered wiring, no fragile silicon, no external points of failure.

This isn't a render or a promise. The full metrology-driven framework is published and falsifiable — measurement, reconstruction, actuation, verification, correction. Read the white paper before you send a single sat:

https://sovertech.org/library

Then fund the hardware: bc1qepqmkem807pu87ryw4pmjy9frqjwne0fpr0rsm
#Bitcoin #HardTech #AtomicScale`,
  },
  {
    key: "followup-the-vault",
    title: "Follow-Up 3 — The Vault (Day 14)",
    desc: "Scale reveal without disclosure — the 169,200+ number does the work.",
    text: `Follow-up — Day 14

One machine. That's all this raise secures.

Behind it sits a secured vault of 169,200+ documented invention concepts — structural systems, lattice architectures, and autonomous frameworks designed from the atomic layer up, spanning every industry that touches physical matter.

The recipes stay locked. The count is public. One production-grade atomic printer turns the archive into hardware.

$1,750,000 in BTC. No VCs. No gatekeepers.
bc1qepqmkem807pu87ryw4pmjy9frqjwne0fpr0rsm

Manifesto, white papers, and live progress:
https://sovertech.org
#BuildInPublic #DeepTech #Bitcoin`,
  },
];

export default function Faq() {
  const [copied, setCopied] = useState(null);

  const copy = async (text, i) => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(i);
      toast.success("Answer copied — fire away");
      setTimeout(() => setCopied(null), 2000);
    } else {
      toast.error("Copy failed — long-press to select instead");
    }
  };

  return (
    <main className="min-h-screen">
      <section className="relative hero-glow noise-overlay py-16 sm:py-20 border-b border-[#1E2638] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-sky-400 mb-4">Objection Handling</p>
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-tight">
            Hard questions. Sharp answers.
          </h1>
          <p className="mt-5 text-slate-400 max-w-2xl leading-relaxed">
            The replies people fire at us on X and Telegram — answered once, on the record.
            Each answer has a copy button so supporters can defend the mission without typing a word.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {FAQS.map((f, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div data-testid={`faq-item-${i + 1}`} className="rounded-2xl border border-[#1E2638] bg-[#131824] p-7 sm:p-9 hover:border-sky-400/40 transition-colors duration-300">
                <div className="flex items-start gap-5 mb-5">
                  <span className="font-mono text-2xl font-bold text-[#1E2638] shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-display font-semibold text-lg sm:text-xl leading-snug flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-sky-400 shrink-0 mt-1" />
                    {f.q}
                  </h2>
                </div>
                <p className="text-slate-300 leading-relaxed pl-0 sm:pl-12">{f.a}</p>
                <div className="mt-6 pl-0 sm:pl-12 flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => copy(f.a, i)}
                    data-testid={`faq-copy-${i + 1}`}
                    className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] rounded-full border px-4 py-2 transition-colors ${
                      copied === i
                        ? "border-emerald-400/60 text-emerald-400"
                        : "border-[#1E2638] text-slate-400 hover:border-sky-400/50 hover:text-sky-300"
                    }`}
                  >
                    <Copy className="w-3.5 h-3.5" /> {copied === i ? "Copied" : "Copy answer"}
                  </button>
                  {f.link && (
                    <Link to={f.link.href} data-testid={`faq-link-${i + 1}`} className="group flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-sky-400 hover:text-sky-300 transition-colors">
                      {f.link.label}
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* ---------- LAUNCH KIT ---------- */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-3">Launch Kit</p>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mb-3">
              Ready-to-post rollout messages
            </h2>
            <p className="text-sm text-slate-400 mb-10 leading-relaxed max-w-2xl">
              The X thread and Telegram drop, pre-written with the $1,750,000 target and the live
              BTC address. Copy, paste, deploy.
            </p>
          </Reveal>
          <div className="space-y-6">
            {LAUNCH_KIT.map((kit) => (
              <Reveal key={kit.key}>
                <div data-testid={`launch-kit-${kit.key}`} className="rounded-2xl border border-[#1E2638] bg-[#131824] overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-4 px-7 py-5 border-b border-[#1E2638]">
                    <div>
                      <h3 className="font-display font-semibold text-lg">{kit.title}</h3>
                      <p className="text-xs text-slate-500 mt-1">{kit.desc}</p>
                    </div>
                    <button
                      onClick={() => copy(kit.text, kit.key)}
                      data-testid={`launch-kit-copy-${kit.key}`}
                      className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] rounded-full border px-4 py-2 transition-colors ${
                        copied === kit.key
                          ? "border-emerald-400/60 text-emerald-400"
                          : "border-amber-400/40 text-amber-400 hover:bg-amber-400/10"
                      }`}
                    >
                      <Copy className="w-3.5 h-3.5" /> {copied === kit.key ? "Copied" : "Copy message"}
                    </button>
                  </div>
                  <pre className="px-7 py-6 text-sm text-slate-300 leading-relaxed whitespace-pre-wrap font-sans">{kit.text}</pre>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <div className="rounded-2xl border border-amber-400/30 bg-[#131824] p-8 sm:p-10 text-center" data-testid="faq-cta">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-3">Still on the fence?</p>
            <h3 className="font-display font-bold text-2xl mb-3">Read the research. Then decide.</h3>
            <p className="text-sm text-slate-400 max-w-xl mx-auto mb-7 leading-relaxed">
              Every white paper is free to read. The framework is falsifiable, the funding
              is on-chain, and the mission is printed in public.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/library" data-testid="faq-cta-library" className="border border-[#1E2638] text-slate-300 font-semibold text-sm rounded-full px-7 py-3.5 hover:border-sky-400/50 hover:text-sky-300 transition-colors">
                Browse the library
              </Link>
              <Link to="/donate" data-testid="faq-cta-donate" className="bg-amber-400 text-[#07090E] font-semibold text-sm rounded-full px-7 py-3.5 hover:bg-amber-300 transition-colors flex items-center gap-2">
                <Flame className="w-4 h-4" /> Fund the hardware
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
